"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.startPasskeyRegistration = exports.startPasskeyAuthentication = exports.setUserFlag = exports.renamePasskey = exports.getUserFlag = exports.getPasskeys = exports.deletePasskey = exports.deleteAllPasskeys = exports.completePasskeyRegistration = exports.completePasskeyAuthentication = void 0;
var _url = _interopRequireDefault(require("@babel/runtime-corejs3/core-js/url"));
var _ = require("../../");
var _webauthnUtils = require("../utils/webauthn-utils");
/**
 * Starts the passkey registration process and gets registration options from the backend
 *
 * @returns Promise containing registration options from the server
 * @throws Error if the API request fails
 */
const startPasskeyRegistration = async () => {
  const {
    data
  } = await _.Ajax.post('/api/v1/passkey/registration/start', {});
  if (!data.success) {
    const error = new Error(data.error || 'Registration start failed');
    error.name = 'PasskeyRegistrationStartError';
    throw error;
  }
  return data;
};

/**
 * Completes the passkey registration process by verifying the attestation and storing the credential
 *
 * @param registrationData - The passkey registration data from WebAuthn
 * @param name - Optional friendly name for the passkey
 * @returns Promise containing the registration response
 * @throws Error if the API request fails
 */
exports.startPasskeyRegistration = startPasskeyRegistration;
const completePasskeyRegistration = async registrationData => {
  const payload = {
    attestation: registrationData
  };
  const {
    data
  } = await _.Ajax.post('/api/v1/passkey/registration/complete', payload);
  if (!data.success) {
    const error = new Error(data.error || 'Registration complete failed');
    error.name = 'PasskeyRegistrationCompleteError';
    error.extraResponseData = data.extraResponseData;
    throw error;
  }
  (0, _webauthnUtils.setKnownPasskeyUser)();
  (0, _webauthnUtils.setAcknowledgedPasskeys)();
  return data;
};

// Authentication API Functions

/**
 * Starts the passkey authentication process and gets authentication options from the backend.
 * An empty POST request is sent to the backend to start the authentication process.
 *
 * @returns Promise containing authentication options and challenge token from the server
 * @throws Error if the API request fails
 */
exports.completePasskeyRegistration = completePasskeyRegistration;
const startPasskeyAuthentication = async () => {
  const {
    data
  } = await _.Ajax.post('/api/v1/passkey/authentication/start', {});
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeyAuthenticationStartError';
    throw error;
  }
  return data;
};

/**
 * Completes the passkey authentication process by verifying the assertion
 *
 * @param authenticationData - The passkey authentication data from WebAuthn including challenge token
 * @returns Promise containing the authentication response with user ID and auth token
 * @throws Error if the API request fails
 */
exports.startPasskeyAuthentication = startPasskeyAuthentication;
const completePasskeyAuthentication = async authenticationData => {
  const url = new _url.default(window.location.href);
  const redirect = url.searchParams.get('r');
  const {
    data
  } = await _.Ajax.post(`/api/v1/passkey/authentication/complete${redirect ? `?redirect=${redirect}` : ''}`, {
    ...authenticationData
  });
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeyAuthenticationCompleteError';
    error.extraResponseData = data.extraResponseData;
    throw error;
  }
  (0, _webauthnUtils.setKnownPasskeyUser)();
  return data;
};

// Management API Functions

/**
 * Retrieves all passkey credentials for the current authenticated user
 *
 * @param userId - ID of the user whose credentials to retrieve
 * @returns Promise containing the list of user's passkey credentials
 * @throws Error if the API request fails
 */
exports.completePasskeyAuthentication = completePasskeyAuthentication;
const getPasskeys = async userId => {
  const {
    data
  } = await _.Ajax.get(`/api/v1/users/${userId}/passkeys`);
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeysGetError';
    throw error;
  }
  return data;
};

/**
 * Updates the friendly name of a specific passkey credential
 *
 * @param userId - ID of the user who owns the credential
 * @param credentialId - The ID of the credential to rename
 * @param name - The new friendly name for the passkey
 * @returns Promise containing the operation response
 * @throws Error if the API request fails
 */
exports.getPasskeys = getPasskeys;
const renamePasskey = async (userId, credentialId, name) => {
  const {
    data
  } = await _.Ajax.patch(`/api/v1/users/${userId}/passkeys/${credentialId}`, {
    name
  });
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeyRenameError';
    throw error;
  }
  return data;
};

/**
 * Deletes a specific passkey credential for the current authenticated user
 *
 * @param userId - ID of the user who owns the credential
 * @param credentialId - The ID of the credential to delete
 * @returns Promise containing the operation response
 * @throws Error if the API request fails
 */
exports.renamePasskey = renamePasskey;
const deletePasskey = async (userId, credentialId) => {
  const {
    data
  } = await _.Ajax.delete(`/api/v1/users/${userId}/passkeys/${credentialId}`);
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeyDeleteError';
    throw error;
  }

  // Check if user has any remaining passkeys after deletion
  try {
    const remainingPasskeys = await getPasskeys(userId);
    if (remainingPasskeys.success && remainingPasskeys.credentials.length === 0) {
      // User has no more passkeys, remove the localStorage flag
      (0, _webauthnUtils.clearKnownPasskeyUser)();
    }
  } catch (error) {
    // If we can't check remaining passkeys, we'll err on the side of caution
    // and not remove the localStorage item to avoid breaking the user experience
    console.warn('Failed to check remaining passkeys after deletion:', error);
  }
  return data;
};

/**
 * Deletes all passkey credentials for the current authenticated user
 *
 * @param userId - The ID of the user whose credentials should be deleted
 * @returns Promise containing the operation response
 * @throws Error if the API request fails
 */
exports.deletePasskey = deletePasskey;
const deleteAllPasskeys = async userId => {
  const {
    data
  } = await _.Ajax.delete(`/api/v1/users/${userId}/passkeys`);
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeysDeleteAllError';
    throw error;
  }

  // When all passkeys are deleted, remove the localStorage flag
  (0, _webauthnUtils.clearKnownPasskeyUser)();
  return data;
};

/**
 * Get the user flag we're using to determine whether or not to recommend passkeys to the user.
 */
exports.deleteAllPasskeys = deleteAllPasskeys;
const getUserFlag = async () => {
  const {
    data
  } = await _.Ajax.get('/api/v1/passkey/reminder-flag');
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeysGetUserFlagError';
    throw error;
  }
  return data;
};

/**
 * Sets the passkey user flag to either 'remind_me_later' or 'passkeys_acknowledged'.
 * If set to 'passkeys_acknowledged', the user will never see the recommend passkeys modal.
 * If set to 'remind_me_later', the user will see the modal again the next time they log in.
 * @param acknowledged
 * @returns
 */
exports.getUserFlag = getUserFlag;
const setUserFlag = async acknowledged => {
  const {
    data
  } = await _.Ajax.post('/api/v1/passkey/reminder-flag', {
    passkeyReminderFlag: acknowledged ? 'passkeys_acknowledged' : 'remind_me_later'
  });
  if (!data.success) {
    const error = new Error(data.error);
    error.name = 'PasskeysSetUserFlagError';
    throw error;
  }
  return data;
};
exports.setUserFlag = setUserFlag;